import {
  CommercialDocumentKindSchema,
  type CommercialDocumentInput,
} from "../../shared/commercial-documents";
import { OpaqueIdSchema } from "../../shared/schemas";

// ADR-0088: five tabs (home, tools, documents, billing, people) plus pages under More.
export type CustomerSection =
  | "home"
  | "tools"
  | "tool"
  | "documents"
  | "notes"
  | "billing"
  | "people"
  | "support"
  | "audit";
export type DocumentFilter = "all" | "contracts" | "knowledge";
export type CommercialDocumentRoute = {
  slug: string;
  section: "document";
} & (
  | { documentId: string; kind?: never }
  | { kind: CommercialDocumentInput["kind"]; documentId?: never }
);
export type CustomerRoute =
  | { slug: string; section: CustomerSection; toolId?: string; filter?: DocumentFilter }
  | CommercialDocumentRoute;

/** Addresses from before ADR-0088 still open the tab that replaced them. */
const LEGACY_SECTIONS: Record<string, { section: CustomerSection; filter?: DocumentFilter }> = {
  overview: { section: "home" },
  organization: { section: "people" },
  knowledge: { section: "documents", filter: "knowledge" },
  agreements: { section: "documents", filter: "contracts" },
  quotes: { section: "documents", filter: "contracts" },
};

function parseDocumentRoute(path: string): CommercialDocumentRoute | null {
  const draft = /^\/customers\/([a-z0-9-]+)\/documents\/new\/([^/]+)\/?$/.exec(path);
  if (draft?.[1]) {
    const kind = CommercialDocumentKindSchema.safeParse(draft[2]);
    return kind.success ? { slug: draft[1], section: "document", kind: kind.data } : null;
  }
  const document = /^\/customers\/([a-z0-9-]+)\/documents\/([^/]+)\/?$/.exec(path);
  if (document?.[1]) {
    const id = OpaqueIdSchema.safeParse(document[2]);
    return id.success ? { slug: document[1], section: "document", documentId: id.data } : null;
  }
  return null;
}

export function parseCustomerRoute(path: string): CustomerRoute | null {
  const document = parseDocumentRoute(path);
  if (document) return document;
  const tool = /^\/customers\/([a-z0-9-]+)\/tools\/([a-z][a-z0-9_]*)\/?$/.exec(path);
  if (tool?.[1] && tool[2]) return { slug: tool[1], section: "tool", toolId: tool[2] };
  const match =
    /^\/customers\/([a-z0-9-]+)(?:\/(tools|documents|notes|billing|people|support|audit|overview|organization|knowledge|agreements|quotes))?\/?$/.exec(
      path,
    );
  if (!match?.[1]) return null;
  const legacy = match[2] ? LEGACY_SECTIONS[match[2]] : undefined;
  if (legacy) return { slug: match[1], ...legacy };
  return { slug: match[1], section: (match[2] as CustomerSection | undefined) ?? "home" };
}
